import React from 'react';
import { Card, CardContent } from '../ui/card';
import { formatRelativeDate, projectLabel, getInitial } from './ipHelpers';

const TOP = 10;

function StoreRow({ item, onOpen, right }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="w-full text-left flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
    >
      <div className="shrink-0 w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-semibold text-gray-600 dark:text-gray-300">
        {getInitial(item.Name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-800 dark:text-gray-100 text-sm truncate">{item.Name}</div>
        <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
          {[item.ShopID && `SAP ${item.ShopID}`, (item.Projects || []).map(projectLabel).slice(0, 1).join('')]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>
      <div className="shrink-0 text-right">{right}</div>
    </button>
  );
}

function Section({ title, subtitle, icon, count, total, onShowMore, children }) {
  return (
    <Card className="flex flex-col min-h-0 overflow-hidden">
      <CardContent className="pt-5 flex flex-col min-h-0 flex-1">
        <div className="mb-3">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-800 dark:text-white">
            {icon}
            {title}
            {typeof count === 'number' && (
              <span className="text-xs font-normal text-gray-400 dark:text-gray-500">({count})</span>
            )}
          </h2>
          {subtitle && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-2 text-left">
              {subtitle}
            </p>
          )}

        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-800 overflow-y-auto min-h-0 flex-1 max-h-80 lg:max-h-none -mr-2 pr-2">
          {children}
        </div>
        {total > TOP && (
          <button
            type="button"
            onClick={onShowMore}
            className="mt-3 self-start shrink-0 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 inline-flex items-center gap-1"
          >
            Voir tout →
          </button>
        )}
      </CardContent>
    </Card>
  );
}

const openBadge = (n) => (
  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
    {n}
  </span>
);

export default function IPStatsLanding({ stats, loading, error, onOpen, onShowMore }) {
  if (loading) {
    return (
      <div className="flex justify-center my-12">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow text-center text-gray-500 dark:text-gray-400">
        Statistiques indisponibles pour le moment. Utilisez la recherche ci-dessus.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:h-[calc(100vh-210px)] lg:grid-rows-1">
      <Section
        title="Derniers magasins visités"
        subtitle="Tous programmes confondus"
        total={stats.lastVisited.length}
        onShowMore={() => onShowMore('last_visit')}
        icon={
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      >
        {stats.lastVisited.slice(0, TOP).map((item, i) => (
          <StoreRow
            key={`lv-${i}`}
            item={item}
            onOpen={onOpen}
            right={<span className="text-xs text-gray-400 dark:text-gray-500">{formatRelativeDate(item.lastVisitDate)}</span>}
          />
        ))}
      </Section>

      <Section
        title="Le plus d'interventions ouvertes"
        subtitle="Magasins à prioriser"
        count={stats.topOpen.length}
        total={stats.topOpen.length}
        onShowMore={() => onShowMore('open_issues')}
        icon={
          <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
          </svg>
        }
      >
        {stats.topOpen.slice(0, TOP).map((item, i) => (
          <StoreRow key={`to-${i}`} item={item} onOpen={onOpen} right={openBadge(`${item.openCount} ouverte${item.openCount > 1 ? 's' : ''}`)} />
        ))}
      </Section>

      <Section
        title="Perfect Store retrouvé"
        subtitle="Toutes interventions récemment clôturées"
        count={stats.recentlyPerfect.length}
        total={stats.recentlyPerfect.length}
        onShowMore={() => onShowMore('perfect_store')}
        icon={
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      >
        {stats.recentlyPerfect.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-2">Aucun magasin récemment redevenu conforme.</p>
        ) : (
          stats.recentlyPerfect.slice(0, TOP).map((item, i) => (
            <StoreRow
              key={`rp-${i}`}
              item={item}
              onOpen={onOpen}
              right={
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                  {formatRelativeDate(item.lastClosedDate)}
                </span>
              }
            />
          ))
        )}
      </Section>
    </div>
  );
}
